'use client';

import React from 'react';
import { Play } from '@/lib/icons';

interface VideoOverlay {
  startTime: number; // Seconds
  endTime: number;   // Seconds
  position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  content: React.ReactNode;
  className?: string;
}

interface VideoSchemaProps {
  name?: string;
  description: string;
  duration?: string; // ISO 8601 duration (PT2M30S)
  uploadDate?: string; // ISO date
  thumbnailUrl?: string;
  embedUrl?: string;
  author?: string | object;
  publisher?: object;
  keywords?: string[];
  category?: string;
  transcript?: string;
}

interface VideoProps {
  url: string;
  title?: string;
  thumbnail?: string;
  aspectRatio?: '16:9' | '4:3' | '1:1' | '9:16';
  className?: string;
  
  // Enhanced controls
  autoPlay?: boolean;        // Default: false
  muted?: boolean;          // Default: true if autoPlay, false otherwise
  controls?: boolean;       // Default: true
  loop?: boolean;           // Default: false
  disablePictureInPicture?: boolean; // Default: true
  controlsList?: string;    // Default: "nodownload"
  preload?: 'none' | 'metadata' | 'auto'; // Default: 'metadata'
  
  // Performance
  lazy?: boolean;           // Default: true
  poster?: string;          // Custom poster frame

  // Strategic overlays for VSLs
  overlays?: VideoOverlay[];
  
  // Manual schema (your template approach)
  schema?: VideoSchemaProps;
}

export const Video: React.FC<VideoProps> = ({ 
  url, 
  title = 'Video', 
  thumbnail,
  aspectRatio = '16:9',
  className = '',
  
  // Enhanced controls with defaults
  autoPlay = false,
  muted = undefined, // Will be set based on autoPlay if not provided
  controls = true,
  loop = false,
  disablePictureInPicture = true,
  controlsList = 'nodownload',
  preload = 'metadata',
  
  // Performance
  lazy = true,
  poster,
  
  // Overlays and schema
  overlays = [],
  schema
}) => {
  // Set muted default based on autoPlay if not explicitly provided
  const videoMuted = muted !== undefined ? muted : autoPlay;
  
  const [currentTime, setCurrentTime] = React.useState(0);
  const [videoRef, setVideoRef] = React.useState<HTMLVideoElement | null>(null);
  const [isIntersecting, setIsIntersecting] = React.useState(!lazy);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Intersection observer for lazy loading
  React.useEffect(() => {
    if (!lazy || !containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [lazy]);

  // Time tracking for overlays
  React.useEffect(() => {
    if (!videoRef || overlays.length === 0) return;

    const handleTimeUpdate = () => {
      setCurrentTime(videoRef.currentTime);
    };

    videoRef.addEventListener('timeupdate', handleTimeUpdate);
    return () => videoRef.removeEventListener('timeupdate', handleTimeUpdate);
  }, [videoRef, overlays.length]);

  // Generate schema.org structured data
  const generateVideoSchema = () => {
    if (!schema) return null;

    const videoSchema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: schema.name || title,
      description: schema.description,
      thumbnailUrl: schema.thumbnailUrl || thumbnail,
      uploadDate: schema.uploadDate,
      duration: schema.duration,
      embedUrl: schema.embedUrl,
      author: schema.author,
      publisher: schema.publisher,
      keywords: schema.keywords,
      category: schema.category,
      transcript: schema.transcript
    };

    // Remove undefined values
    Object.keys(videoSchema).forEach(key => {
      if (videoSchema[key as keyof typeof videoSchema] === undefined) {
        delete videoSchema[key as keyof typeof videoSchema];
      }
    });

    return videoSchema;
  };

  const schemaData = generateVideoSchema();

  // Extract video ID and determine platform
  const getVideoInfo = (url: string) => {
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) {
      return {
        platform: 'youtube',
        id: youtubeMatch[1],
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
        thumbnailUrl: thumbnail || `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`
      };
    }

    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return {
        platform: 'vimeo',
        id: vimeoMatch[1],
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
        thumbnailUrl: thumbnail || ''
      };
    }

    // Default: assume direct URL
    return {
      platform: 'direct',
      id: '',
      embedUrl: url,
      thumbnailUrl: thumbnail || ''
    };
  };

  const videoInfo = getVideoInfo(url);
  const isVideoFile = url.match(/\.(mp4|webm|ogg|mov)$/i);
  const [showVideo, setShowVideo] = React.useState(!videoInfo.thumbnailUrl || !!isVideoFile);

  const aspectRatioClasses = {
    '16:9': 'pb-[56.25%]',
    '4:3': 'pb-[75%]',
    '1:1': 'pb-[100%]',
    '9:16': 'pb-[177.78%]'
  };

  // Get currently visible overlays
  const visibleOverlays = overlays.filter(
    overlay => currentTime >= overlay.startTime && currentTime <= overlay.endTime
  );

  return (
    <>
      {/* Schema.org structured data */}
      {schemaData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      )}
      
      <div 
        ref={containerRef}
        className={`my-8 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 ${className}`}
      >
      <div className={`relative rounded-lg overflow-hidden shadow-elevation-2 bg-black ${aspectRatioClasses[aspectRatio]}`}>
        {showVideo && isIntersecting ? (
          isVideoFile ? (
            <video
              ref={setVideoRef}
              className="absolute inset-0 w-full h-full object-contain"
              src={url}
              title={title}
              controls={controls}
              autoPlay={autoPlay}
              muted={videoMuted}
              loop={loop}
              playsInline
              disablePictureInPicture={disablePictureInPicture}
              controlsList={controlsList}
              preload={preload}
              poster={poster}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <iframe
              className="absolute inset-0 w-full h-full"
              src={videoInfo.embedUrl}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <div 
            className="absolute inset-0 cursor-pointer group"
            onClick={() => setShowVideo(true)}
          >
            <img 
              src={videoInfo.thumbnailUrl} 
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center">
              <div className="bg-white bg-opacity-90 rounded-full p-4 group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-primary fill-current" />
              </div>
            </div>
          </div>
        )} 
        
        {!isIntersecting && lazy && (
          // Lazy loading placeholder
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
            <span className="text-gray-500">Loading video...</span>
          </div>
        )}
        
        {/* Time-based overlays */}
        {visibleOverlays.map((overlay, index) => (
          <div
            key={index}
            className={`absolute z-10 pointer-events-auto ${overlay.className || ''}`}
            style={{
              top: overlay.position.top,
              bottom: overlay.position.bottom,
              left: overlay.position.left,
              right: overlay.position.right,
              transform: overlay.position.left === '50%' ? 'translateX(-50%)' : undefined,
            }}
          >
            {overlay.content}
          </div>
        ))}
      </div>
    </div>
    </>
  );
};